import { Document, Page, Text, View, StyleSheet, Image, Link } from '@react-pdf/renderer';
import { format, parseISO } from 'date-fns'; 
import React from 'react';

const styles = StyleSheet.create({
  page: {
    padding: 30,
    backgroundColor: '#FAFAF7',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
    paddingBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#4CAF50',
  },
  logoAndContact: {
    flexDirection: 'row',
    flex: 1,
    alignItems: 'center',
    fontWeight: 'bold',
  },
  logo: {
    width: 140,
    height: 60,
    marginRight: 10,
  },
  contactInfo: {
    fontSize: 9,
    color: '#37474F',
    fontWeight: 'bold',
    lineHeight: 1.4,
    textAlign: 'right',
  },
  websiteLink: {
    color: '#FF0000',
  },
  serviceReportBox: {
    backgroundColor: '#2F5E1E',
    padding: '10px 20px',
    borderRadius: 4,
    justifyContent: 'center',
    height: 60,
  },
  serviceReportText: {
    fontSize: 14,
    color: '#FFFFFF',
    fontWeight: 'bold',
    textDecoration: 'underline',
  },
  detailsSection: {
    marginTop: 10,
    paddingLeft: 5,
  },
  detailText: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#37474F',
  },
  contentGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
    marginTop: 10,
  },
  gridItem: {
  width: '48%',
  height: 160, // or even 140 for tighter layout
  marginBottom: 15,
  backgroundColor: '#F5F5F5',
  padding: 0,
},

  servicesSection: {
    padding: 10,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#1B5E20',
    marginBottom: 6,
  },
  text: {
    fontSize: 11,
    color: '#37474F',
    lineHeight: 1.6,
  },
  list: {
    paddingLeft: 10,
  },
  listItem: {
    fontSize: 11,
    marginBottom: 3,
    color: '#37474F',
    flexDirection: 'row',
    alignItems: 'center',
  },
  bulletPoint: {
    fontSize: 11,
    marginRight: 4,
    color: '#37474F',
  },
  notesSection: {
    width: '48%',
    backgroundColor: '#F5F5F5',
    padding: 10,
    marginBottom: 15,
  },
  notesTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#1B5E20',
    marginBottom: 6,
  },
  notesText: {
    fontSize: 11,
    color: '#37474F',
    lineHeight: 1.6,
  },
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  footer: {
    marginTop: 20,
    alignItems: 'center',
  },
  socialIconsContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 10,
    marginTop: 10,
  },
  socialIcon: {
    width: 30,
    height: 30,
  },
});

const JobPDF = ({ formData, base64Images }) => {
  console.log('JobPDF received base64Images:', base64Images?.length);

  const {
    property,
    customPropertyName,
    date,
    services,
    customServiceDescription,
    imageUrls,
    notes,
  } = formData;

  let normalizedCustomPropertyName = [];
if (Array.isArray(customPropertyName)) {
  normalizedCustomPropertyName = customPropertyName;
} else if (typeof customPropertyName === 'string') {
  normalizedCustomPropertyName = [customPropertyName];
}

  const formattedDate = format(
    typeof date === 'string' ? parseISO(date) : new Date(date),
    'MM/dd/yyyy'
  );

  // Convert services to an array and replace "Other" with the custom description if present
  const serviceList = Array.isArray(services)
    ? services
    : services.split(',').map(service => service.trim());

  const resolvedServiceList = serviceList.map(service =>
    service === 'Other' && customServiceDescription ? customServiceDescription : service
  );

  const imageKeys = [
  'image',
  'secondImage',
  'thirdImage',
  'fourthImage',
  'fifthImage',
  'sixthImage',
  'seventhImage',
  'eighthImage'
];

const images = imageKeys
  .map(key => imageUrls?.[key])
  .filter(Boolean)
  .map(url =>
    url.includes('firebasestorage.googleapis.com') && !url.includes('alt=media')
      ? `${url}?alt=media`
      : url
  );


  const allImages = base64Images?.length > 0 ? base64Images : images;

  

  return (
  <Document>
    {/* Page 1: Header + Info + Services + Notes */}
    <Page size="A4" style={styles.page}>
      <View style={styles.header}>
        <View style={styles.logoAndContact}>
          <Image style={styles.logo} src="/rciLogo.png" />
          <View style={styles.contactInfo}>
            <Text style={styles.websiteLink}>www.RotoloConsultants.com</Text>
            <Text>Phone: (800) 641-2427</Text>
          </View>
        </View>
        <View style={styles.serviceReportBox}>
          <Text style={styles.serviceReportText}>SERVICE REPORT</Text>
        </View>
      </View>

      <View style={styles.detailsSection}>
        <Text style={styles.detailText}>Property Name: {property}</Text>
        {normalizedCustomPropertyName.map((name, index) => (
          <Text key={index} style={styles.text}>{name}</Text>
        ))}
        <Text style={styles.detailText}>Service Date: {formattedDate}</Text>
      </View>

      <View style={styles.contentGrid}>
        <View style={styles.gridItem}>
          <View style={styles.servicesSection}>
            <Text style={styles.sectionTitle}>Completed Services</Text>
            <View style={styles.list}>
              {resolvedServiceList.map((service, index) => (
                <View key={index} style={styles.listItem}>
                  <Text style={styles.bulletPoint}>•</Text>
                  <Text>{service}</Text>
                </View>
              ))}
            </View>
          </View>
        </View>

        <View style={styles.notesSection}>
          <Text style={styles.notesTitle}>Notes</Text>
          <Text style={styles.notesText}>
            {notes || 'No notes available'}
          </Text>
        </View>
      </View>
    </Page>

     {/* Pages 2+: Images */}
      {Array.from({ length: Math.ceil(allImages.length / 4) }).map((_, pageIndex) => {
        const imageChunk = allImages.slice(pageIndex * 4, pageIndex * 4 + 4);
        const isLastPage = pageIndex === Math.floor((allImages.length - 1) / 4);

        return (
          <Page key={`image-page-${pageIndex}`} size="A4" style={styles.page}>
            <View style={styles.contentGrid}>
              {imageChunk.map((image, index) => (
                <View key={index} style={styles.gridItem}>
                  <Image src={image} style={styles.image} />
                </View>
              ))}
            </View>

            {isLastPage && (
              <View style={styles.footer}>
                <Text>Follow us on:</Text>
                <View style={styles.socialIconsContainer}>
                  <Link src="https://www.linkedin.com/company/rotoloconsultants">
                    <Image src="/linkedinLogo.png" style={styles.socialIcon} />
                  </Link>
                  <Link src="https://www.instagram.com/rotoloconsultantsinc/">
                    <Image src="/instagramLogo.png" style={styles.socialIcon} />
                  </Link>
                  <Link src="https://www.facebook.com/RotoloConsultants/">
                    <Image src="/facebookLogo.png" style={styles.socialIcon} />
                  </Link>
                </View>
              </View>
            )}
          </Page>
        );
      })}
    </Document>
  );

};

export { JobPDF };

